import type { RouteComponentProps } from '@reach/router';
import type { FormikHelpers, FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import RoundedButton from '@components/RoundedButton/index';
import TextInputBox from '@components/TextInputBox';

import * as ROUTES from '@constants/routes';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';
import { useNavigate } from '@hooks/useNavigate';
import { useSnackbar } from '@hooks/useSnackbar';

import AuthLayout, {
  BackLink,
  HorizontalRule,
  Subtitle,
} from '@layouts/AuthLayout';

import { useForgotPasswordMutation } from 'generated/session.generated';

const messages = defineMessages({
  header: {
    id: 'passwordReset.emailForm.header',
    defaultMessage: 'Reset Password',
  },
  subheader: {
    id: 'passwordReset.emailForm.asdf',
    defaultMessage:
      "Enter your email and we'll send you a code to get back into your account.",
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  notFound: {
    id: 'passwordReset.emailForm.notFound',
    defaultMessage: 'That email does not exist in our system.',
  },
  exampleEmail: {
    id: 'passwordReset.emailForm.exampleEmail',
    defaultMessage: 'example@email.com',
  },
  goBack: {
    id: 'passwordReset.emailForm.goBack',
    defaultMessage: 'Back to login',
  },
  buttonText: {
    id: 'passwordReset.emailForm.buttonText',
    defaultMessage: 'SEND LOGIN CODE',
  },
  required: {
    id: 'passwordReset.emailForm.required',
    defaultMessage: 'Email required',
  },
  sendingError: {
    id: 'passwordReset.emailForm.sendingError',
    defaultMessage: 'There was an error sending your password reset email',
  },
  awaitingVerification: {
    id: 'passwordReset.emailForm.awaitingVerification',
    defaultMessage: 'Could not verify reCaptcha',
  },
  codeSent: {
    id: 'passwordReset.emailForm.codeSent',
    defaultMessage: 'Code sent! Please check your email',
  },
});

type EmailFormValues = {
  email: string;
};

const EmailForm: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { emailSchema } = useCommonSchemas();
  const [forgotPassword] = useForgotPasswordMutation();
  const { navigateRoute } = useNavigate();

  // Validation schema
  const ResetFormSchema = Yup.object().shape({
    email: emailSchema.required(formatMessage(messages.required)),
  });

  async function handleSubmit(
    { email }: EmailFormValues,
    { setErrors }: FormikHelpers<EmailFormValues>
  ) {
    const response = await forgotPassword({ variables: { input: { email } } });

    if (response.errors) {
      setErrors({ email: formatMessage(messages.sendingError) });
      return;
    }
    const responseMessage = response.data?.forgotPassword.message;
    enqueueSnackbar({
      message: responseMessage || formatMessage(messages.codeSent),
      variant: 'success',
    });
    navigateRoute(
      ROUTES.FORGOT_PASSWORD_RESET,
      { email },
      { state: { deliveryMessage: responseMessage } }
    );
  }

  return (
    <AuthLayout title={formatMessage(messages.header)}>
      <Subtitle>{formatMessage(messages.subheader)}</Subtitle>

      <Formik
        enableReinitialize
        initialValues={{
          email: '',
        }}
        onSubmit={handleSubmit}
        validationSchema={ResetFormSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <Field name="email">
              {({ field, meta }: FieldProps<string>) => (
                <TextInputBox
                  hasError={Boolean(meta.touched && meta.error)}
                  label={formatMessage(messages.email)}
                  helperText={meta.touched ? meta.error : undefined}
                  placeholder={formatMessage(messages.exampleEmail)}
                  required
                  type="email"
                  {...field}
                />
              )}
            </Field>

            <HorizontalRule />

            <RoundedButton disabled={isSubmitting} type="submit">
              {formatMessage(messages.buttonText)}
            </RoundedButton>
          </Form>
        )}
      </Formik>
      <BackLink to={ROUTES.LOGIN}>{formatMessage(messages.goBack)}</BackLink>
    </AuthLayout>
  );
};

export default EmailForm;
